/**
 * Backend Auth API Endpoints
 */

import h from '@/lib/helpers';
import axios from '@/api/axios';

/**
 * Retrieve auth token for guest
 * @param headers
 * @returns {Promise}
 */
function getGuestToken({ headers = {} }) {
  return axios.post('/auth/guest/', {}, { headers });
}

/**
 * Retrieve auth token for user
 * @param credentials
 * @param headers
 * @returns {Promise}
 */
function getCustomerToken({
  credentials = h.mandatory('credentials'),
  headers = {},
}) {
  return axios.post('/auth/user/', credentials, { headers });
}

/**
 * Retrieve auth token kiosk
 * @param credentials (kiosk secret and phone number)
 * @param headers
 * @returns {Promise}
 */
function getKioskToken({
  credentials = h.mandatory('credentials'),
  headers = {},
}) {
  const data = {
    phone:        credentials.phone,
    kiosk_secret: h.getKioskSecret(),
  };

  return axios.post('/auth/kiosk/', data, { headers });
}

/**
 * Retrieve customer profile
 * @param headers
 * @returns {Promise}
 */
function customerProfile({ headers = {} }) {
  return axios.get('/profile/', { headers });
}

/**
 * Retrieve customer profile
 * @param {String} phone - users phone for check
 * @param headers
 * @returns {Promise}
 */
function checkPhone({ phone = h.mandatory('phone'), headers = {} }) {
  return axios.post('/profile/check/', { phone },  { headers });
}

/**
 * Check customer profile.
 * @param {number} phone
 * @return {Promise<AxiosResponse<any>>}
 */
function checkCustomer({ phone = h.mandatory('phone') }) {
  return axios.post('/profile/customer/check/', { phone });
}

/**
 * Reset password of user via email
 * @param {String} email - users email for reset password
 * @param headers
 * @returns {Promise}
 */
function resetPassword({ email = h.mandatory('email'), headers = {} }) {
  return axios.post('/auth/password/reset/', { email },  { headers });
}


/**
 * Confirm reset password of user via email
 * @param {String} uid - uid for confirm
 * @param {String} token - token for confirm
 * @param {String} password - new password of user
 * @param headers
 * @returns {Promise}
 */
function confirmResetPassword({
  uid = h.mandatory('uid'),
  token = h.mandatory('token'),
  password = h.mandatory('password'), headers = {},
}) {
  return axios.post('/auth/password/reset/confirm/', { uid, token, password },  { headers });
}

/**
 * Refresh auth token if token was expired
 * @param headers
 * @returns {Promise}
 */
function refreshToken({ headers = {} }) {
  return axios.post('/auth/refresh_token/', {}, { headers });
}

/**
 * Push FCM token.
 * @param {string} token
 * @return {Promise<AxiosResponse<any>>}
 */
function pushDeviceFcmToken(token) {
  return axios.post('/devices/', {
    registration_id:    token,
    cloud_message_type: 'FCM',
  });
}

/**
 * Sends a request to generate an email with account removal confirmation.
 */
export function requestAccountRemoval() {
  return axios.post('/profile/delete/send_validation_code/');
}

/**
 * Removes the account of the user.
 * @param {number} userId Id of a user that will be removed.
 * @param {string} code A confirmation code that was sent to the user by email.
 */
export function confirmAccountRemoval(code) {
  return axios.delete('/profile/delete/delete_user/', { data: { verification_code: code } });
}

export default {
  getGuestToken,
  getCustomerToken,
  customerProfile,
  checkPhone,
  checkCustomer,
  getKioskToken,
  resetPassword,
  confirmResetPassword,
  refreshToken,
  pushDeviceFcmToken,
  requestAccountRemoval,
  confirmAccountRemoval,
};



// WEBPACK FOOTER //
// ./src/apps/auth/api/index.js